<script lang="ts" setup>
import { getTradeUrl } from '@/utils'
import { useTzDate } from '@/composable/useTzDate'
import { capitalize } from 'lodash'
import { Skeleton } from 'primevue'
import Badge from 'primevue/badge'
import Card from 'primevue/card'
import Button from 'primevue/button'
import { computed, ref, watch } from 'vue'
import { i18n } from '@/i18n'

export interface DateEvents {
  time: string
  isListed: boolean
  asset: string
  severity?: string
  exchange: string
  diff: number
  swap: boolean
  startAt: number
  eventType: 'listing' | 'delisting'
}

const props = defineProps<{
  value: Record<string, DateEvents[]>
  loading: boolean
}>()

const { yyyymmdd, getDayOfWeek, now } = useTzDate()

const sortedEvents = (events: DateEvents[]): DateEvents[] => {
  return [...events].sort((a, b) => a.asset.localeCompare(b.asset))
}

const isToday = (dateStr: string): boolean => {
  return dateStr === yyyymmdd(new Date())
}

const isTomorrow = (dateStr: string): boolean => {
  const t = new Date()
  t.setDate(t.getDate() + 1)
  return dateStr === yyyymmdd(t)
}

const isYesterday = (dateStr: string): boolean => {
  const t = new Date()
  t.setDate(t.getDate() - 1)
  return dateStr === yyyymmdd(t)
}

const formatDateHeader = (dateStr: string): string => {
  if (isToday(dateStr)) return i18n.global.t('listings.today')
  if (isTomorrow(dateStr)) return i18n.global.t('listings.tomorrow')
  if (isYesterday(dateStr)) return i18n.global.t('listings.yesterday')
  const date = new Date(dateStr.split('.').reverse().join('-'))
  return i18n.global.t(`days.${getDayOfWeek(date)}`)
}

const timeAgo = (diffSec: number): string => {
  const days = Math.floor(diffSec / 86400)
  const hours = Math.floor((diffSec % 86400) / 3600)
  const mins = Math.floor((diffSec % 3600) / 60)
  if (days > 0) return i18n.global.t('listings.daysAgo', { days, hours })
  if (hours > 0) return i18n.global.t('listings.hoursAgo', { hours, mins })
  return i18n.global.t('listings.minsAgo', { mins })
}

const countdownStr = (targetTs: number): string => {
  const diffSec = Math.floor(targetTs / 1000 - now.value.getTime() / 1000)
  if (diffSec <= 0) return '00:00:00'
  const days = Math.floor(diffSec / 86400)
  const hours = Math.floor((diffSec % 86400) / 3600)
  const mins = Math.floor((diffSec % 3600) / 60)
  const secs = Math.floor(diffSec % 60)
  const pad = (n: number) => n.toString().padStart(2, '0')
  if (days > 0) return `${days}${i18n.global.t('listings.daysShort')} ${pad(hours)}:${pad(mins)}:${pad(secs)}`
  return `${pad(hours)}:${pad(mins)}:${pad(secs)}`
}

const dateKeys = computed(() => {
  return Object.keys(props.value).sort((a, b) => {
    const dateA = new Date(a.split('.').reverse().join('-'))
    const dateB = new Date(b.split('.').reverse().join('-'))
    return dateB.getTime() - dateA.getTime()
  })
})

const collapsedDays = ref<Set<string>>(new Set())

const toggleDay = (dateStr: string) => {
  const set = new Set(collapsedDays.value)
  if (set.has(dateStr)) {
    set.delete(dateStr)
  } else {
    set.add(dateStr)
  }
  collapsedDays.value = set
}

watch(
  dateKeys,
  (keys) => {
    const set = new Set<string>()
    keys.forEach((key) => {
      if (!isToday(key)) {
        set.add(key)
      }
    })
    collapsedDays.value = set
  },
  { immediate: true },
)
</script>

<template>
  <Card class="shadow-sm overflow-hidden" :pt="{ body: { class: 'p-0' } }">
    <template #content>
      <!-- Header row -->
      <div
        class="hidden sm:grid grid-cols-[auto_minmax(0,1.5fr)_120px_120px_140px_auto] gap-x-3 px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-color ">
        <div class="w-1"></div>
        <span>{{ $t('table.asset') }}</span>
        <span>{{ $t('filters.exchange') }}</span>
        <span>{{ $t('listings.marketType') }}</span>
        <span class="text-right">{{ $t('table.time') }}</span>
        <div></div>
      </div>

      <!-- Skeleton -->
      <template v-if="loading">
        <div v-for="g in 3" :key="g">
          <div class="px-3 py-2 border-b border-color flex items-center gap-2">
            <Skeleton width="100px" height="16px" />
            <Skeleton width="70px" height="12px" />
          </div>
          <div v-for="i in 6" :key="i"
            class="grid grid-cols-[auto_minmax(0,1.5fr)_120px_120px_140px_auto] gap-x-3 px-3 py-1.5 border-b border-color items-center">
            <div class="w-1 h-6 rounded-full bg-gray-300 self-center"></div>
            <div class="flex flex-col gap-0.5">
              <Skeleton width="70px" height="13px" />
              <Skeleton width="40px" height="11px" />
            </div>
            <Skeleton width="60px" height="16px" />
            <Skeleton width="45px" height="14px" />
            <div class="flex flex-col gap-0.5 items-end">
              <Skeleton width="70px" height="13px" />
              <Skeleton width="50px" height="11px" />
            </div>
            <Skeleton width="10px" height="10px" class="self-center" />
          </div>
        </div>
      </template>

      <template v-else>
        <div v-for="(dateKey, index) in dateKeys" :key="dateKey">
          <!-- Date header -->
          <div :class="[
            'flex items-center gap-2 px-3 py-2 border-color cursor-pointer select-none bg-[var(--p-content-background)]',
            index === dateKeys.length - 1 && collapsedDays.has(dateKey) ? 'border-b-0' : 'border-b-1',
          ]" @click="toggleDay(dateKey)">
            <div class="w-1.5 h-5 rounded-full bg-primary"></div>
            <span class="font-bold text-sm">{{ formatDateHeader(dateKey) }}</span>
            <span class="text-xs text-gray-500">{{ dateKey }}</span>
            <div class="ms-auto flex items-center gap-2">
              <Badge v-if="isToday(dateKey)" :value="$t('listings.today')" severity="success" />
              <Badge v-else-if="isTomorrow(dateKey)" :value="$t('listings.tomorrow')" severity="info" />
              <Badge v-else-if="isYesterday(dateKey)" :value="$t('listings.yesterday')" severity="secondary" />
              <Button size="small" rounded variant="text"
                :icon="collapsedDays.has(dateKey) ? 'pi pi-plus' : 'pi pi-minus'" />
            </div>
          </div>

          <!-- Rows -->
          <template v-if="!collapsedDays.has(dateKey)">
            <template v-if="(value[dateKey] ?? []).length === 0">
              <div :class="[
                'px-3 py-4 text-center text-gray-400 text-sm border-color',
                index === dateKeys.length - 1 ? 'border-b-0' : 'border-b',
              ]">
                {{ $t('listings.noListingsForDay') }}
              </div>
            </template>
            <template v-else>
              <a v-for="(item, itemIndex) in sortedEvents(value[dateKey] ?? [])"
                :key="`${item.asset}-${item.exchange}-${item.time}`"
                :href="getTradeUrl(item.exchange, item.asset, item.swap)" target="_blank" :class="[
                  'listing-row group grid grid-cols-[auto_minmax(0,1.5fr)_120px_120px_140px_auto] gap-x-3 px-3 py-1.5 border-color transition-colors cursor-pointer no-underline text-[var(--p-card-color)] items-center',
                  index === dateKeys.length - 1 && itemIndex === ((value[dateKey] ?? []).length - 1) ? 'border-b-0' : 'border-b',
                ]">

                <!-- Market type accent -->
                <div class="w-1 h-8 rounded-full self-center"
                  :class="item.swap ? 'bg-[var(--sr-warn)]' : 'bg-[var(--sr-success)]'"></div>

                <!-- Asset / Time -->
                <div class="flex flex-col min-w-0 gap-0.5">
                  <span class="font-semibold text-sm truncate flex items-center gap-1.5">
                    {{ item.asset }}
                    <span class="text-[11px] text-gray-500">/ USDT</span>
                    <span
                      class="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                      :class="item.eventType === 'delisting'
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                        : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'">
                      {{ item.eventType === 'delisting' ? $t('listings.delistingBadge') : $t('listings.listingBadge') }}
                    </span>
                  </span>
                  <span class="text-[11px] text-gray-400">{{ item.time }}</span>
                </div>

                <!-- Exchange -->
                <span
                  class="text-xs font-semibold bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 px-2 py-0.5 rounded-md justify-self-start">
                  {{ capitalize(item.exchange) }}
                </span>

                <!-- Market Type -->
                <span class="text-xs font-semibold" :class="item.swap
                  ? 'text-orange-600 dark:text-orange-400'
                  : 'text-emerald-600 dark:text-emerald-400'">
                  {{ item.swap ? $t('listings.futuresShort') : $t('listings.spotShort') }}
                </span>

                <!-- Countdown / Status -->
                <div class="flex flex-col items-end text-right gap-0.5">
                  <template v-if="item.diff > 0">
                    <span class="text-xs font-semibold"
                      :class="item.eventType === 'delisting' ? 'text-red-500' : 'text-gray-500'">
                      {{ item.eventType === 'delisting' ? $t('listings.alreadyDelisted') : $t('listings.alreadyTrading')
                      }}
                    </span>
                    <span class="text-[10px] text-gray-400">{{ timeAgo(item.diff) }}</span>
                  </template>
                  <template v-else>
                    <span class="text-xs font-mono font-bold"
                      :class="item.eventType === 'delisting' ? 'text-red-500' : 'text-primary'">
                      {{ countdownStr(item.startAt) }}
                    </span>
                    <span class="text-[10px] text-gray-400">
                      {{ item.eventType === 'delisting' ? $t('listings.untilDelisting') : $t('listings.untilListing') }}
                    </span>
                  </template>
                </div>

                <!-- External link icon -->
                <!-- <i
                  class="pi pi-external-link text-gray-300 group-hover:text-primary transition-colors text-[10px] self-center justify-self-end"></i> -->
              </a>
            </template>
          </template>
        </div>

        <div v-if="dateKeys.length === 0" class="text-center py-20">
          <i class="pi pi-calendar-times text-5xl text-gray-300 mb-4 block"></i>
          <p class="text-gray-400 text-sm">{{ $t('listings.noData') }}</p>
        </div>
      </template>
    </template>
  </Card>
</template>

<style scoped>
.listing-row {
  background-color: transparent;
}

.listing-row:hover {
  background-color: var(--sr-zebra-table-color-hover);
}
</style>
